"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { QRCodeSVG } from "qrcode.react";
import { useRouter } from "next/navigation";
import { AppContext } from "@/src/context/AppContext";
import { GameContext } from "@/src/context/GameContext";
import { Deposit } from "@/src/services/payments";
import {
  ActivateCoupon,
  CancelCoupon,
  GetActiveCoupon,
} from "@/src/services/coupon";
import { isEmailVerifiedForCoupon } from "@/src/lib/emailVerification";
import { FaChevronDown, FaWallet } from "react-icons/fa";
import WalletDepositModal, {
  openLightningWallet,
} from "@/src/components/WalletDepositModal";
import {
  descriptionHasTerm,
  formatCouponTerms,
  getCouponRedeemType,
  isAvailableCoupon,
  isCouponAmountEligible,
  mapPlayerCoupon,
  normalizeCouponCode,
  resolveActiveCoupon,
  resolveCouponWarning,
  resolveIsCouponActivated,
  getUserKey,
  getCouponErrorMessage,
  isCouponConflictError,
} from "@/src/lib/couponHelpers";
import {
  buildGamePathFromDisplayName,
  canOpenFreeSpinModal,
  getFreeSpinDetailsFromCoupon,
  mapActiveCouponToFreeSpinGame,
} from "@/src/lib/freeSpin";

const BASE_DEPOSIT_MIN = 10;
const QUICK_AMOUNTS = ["20", "50", "100"];
const CRYPTO_OPTIONS = ["BTC", "LTC", "ETH", "BCH", "USDT", "BNB"];

const EMAIL_UNVERIFIED = "EMAIL_UNVERIFIED";

function renderCouponTermLine(line, key) {
  const text = String(line ?? "").trim();
  if (!text) return null;
  return <li key={key}>{text}</li>;
}

function copyText(value) {
  const text = String(value ?? "");
  if (!text) return Promise.resolve(false);

  if (navigator?.clipboard?.writeText) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return Promise.resolve(ok);
  } catch {
    return Promise.resolve(false);
  }
}

function getPaymentErrorMessage(error) {
  return (
    error?.data?.message ||
    error?.response?.data?.message ||
    error?.message ||
    "Deposit failed. Please try again."
  );
}

export default function QuickDepositSection() {
  const router = useRouter();
  const {
    user,
    isLoadingUser,
    isRefreshingUserProfile,
    setIsClosed,
    setPageLoader,
    paymentResponse,
    setPaymentResponse,
    errorMessagePayment,
    setErrorMessagePayment,
    activeCoupon,
    setActiveCoupon,
    setIsCouponActivated,
    playerCoupons,
    setFreeSpinGame,
    games,
    refreshCouponData,
  } = useContext(AppContext);
  const { startGame, gameLoader } = useContext(GameContext);

  const [amount, setAmount] = useState("50");
  const [cryptoCurrencyCode, setCryptoCurrencyCode] = useState("BTC");
  const [selectedCrypto, setSelectedCrypto] = useState("BTC");
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [promotionsError, setPromotionsError] = useState(null);
  const [expandedCouponCode, setExpandedCouponCode] = useState("");
  const [activatingCouponCode, setActivatingCouponCode] = useState("");
  const [couponWarning, setCouponWarning] = useState(null);
  const [errorCouponMessage, setErrorCouponMessage] = useState(null);
  const [successCouponMessage, setSuccessCouponMessage] = useState(null);
  const [amountError, setAmountError] = useState(null);
  const [cryptoError, setCryptoError] = useState(null);
  const [bonusCode, setBonusCode] = useState("");
  const [bonusCodeOpen, setBonusCodeOpen] = useState(true);
  const [bonusApplying, setBonusApplying] = useState(false);
  const [isFirstDepositClick, setIsFirstDepositClick] = useState(false);
  const [isStartingFreeSpin, setIsStartingFreeSpin] = useState(false);
  const [walletDeposit, setWalletDeposit] = useState(null);
  const closeWalletDeposit = useCallback(() => setWalletDeposit(null), []);
  const [showLabel, setShowLabel] = useState(false);
  const [showLabelAmount, setShowLabelAmount] = useState(false);

  const paymentResultRef = useRef(null);
  const fetchInFlightRef = useRef(false);
  const fetchedForUserRef = useRef(undefined);
  const freeSpinPlayLockRef = useRef(false);
  const prevUserKeyRef = useRef("");
  const retryTimeoutRef = useRef(null);
  const loadGenerationRef = useRef(0);
  const mountedRef = useRef(true);

  const userKey = getUserKey(user) || "";

  const clearLocalMessages = useCallback(() => {
    setCouponWarning(null);
    setErrorCouponMessage(null);
    setSuccessCouponMessage(null);
    setAmountError(null);
    setCryptoError(null);
    setErrorMessagePayment(null);
  }, [setErrorMessagePayment]);

  const resetFormState = useCallback(() => {
    loadGenerationRef.current += 1;
    if (retryTimeoutRef.current != null) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    setAmount("");
    setCryptoCurrencyCode("");
    setSelectedCrypto("");
    setSelectedCoupon(null);
    setPromotions([]);
    setPromotionsLoading(false);
    setPromotionsError(null);
    setExpandedCouponCode("");
    setActivatingCouponCode("");
    setBonusCode("");
    setBonusCodeOpen(false);
    setBonusApplying(false);
    setIsFirstDepositClick(false);
    setIsStartingFreeSpin(false);
    setShowLabel(false);
    setShowLabelAmount(false);
    setPaymentResponse(null);
    clearLocalMessages();
    fetchedForUserRef.current = undefined;
    fetchInFlightRef.current = false;
  }, [clearLocalMessages, setPaymentResponse]);

  const syncPromotionsFromList = useCallback((list) => {
    const mapped = (Array.isArray(list) ? list : [])
      .filter(isAvailableCoupon)
      .map(mapPlayerCoupon);
    setPromotions(mapped);
    setPromotionsError(null);
  }, []);

  const loadPromotions = useCallback(async () => {
    if (!userKey || fetchInFlightRef.current) return;

    fetchInFlightRef.current = true;
    setPromotionsLoading(true);
    setPromotionsError(null);

    const requestUserKey = userKey;
    const generation = ++loadGenerationRef.current;
    const maxAttempts = 3; // initial + 2 transient retries

    const isCurrent = () =>
      mountedRef.current && generation === loadGenerationRef.current;

    const wait = (ms) =>
      new Promise((resolve) => {
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          resolve();
        }, ms);
      });

    try {
      if (typeof refreshCouponData !== "function") {
        throw new Error("Coupon refresh is unavailable.");
      }

      let lastError = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        if (!isCurrent()) return;

        const result = await refreshCouponData({ userKey: requestUserKey });
        if (!isCurrent()) return;

        // result.ok tracks GetPlayerCoupons only; active 204/null is fine.
        if (result?.ok) {
          const list = Array.isArray(result.playerCoupons)
            ? result.playerCoupons
            : [];
          // Successful API result (including empty list) — not bootstrap [].
          syncPromotionsFromList(list);
          fetchedForUserRef.current = requestUserKey;
          setPromotionsError(null);
          return;
        }

        lastError = result?.error || new Error("Failed to load promotions");

        const transient =
          result?.transient === true ||
          (() => {
            const status =
              lastError?.response?.status ?? lastError?.status ?? null;
            if (status === 401 || status === 403) return true;
            const message = String(lastError?.message || "").toLowerCase();
            return (
              message.includes("stale coupon refresh discarded") ||
              message.includes("network error") ||
              message.includes("timeout")
            );
          })();

        if (!transient || attempt >= maxAttempts) {
          break;
        }

        await wait(250 * attempt);
      }

      if (!isCurrent()) return;
      setPromotionsError(lastError || new Error("Failed to load promotions"));
      setPromotions([]);
    } catch (error) {
      if (!isCurrent()) return;
      setPromotionsError(error);
      setPromotions([]);
    } finally {
      if (generation === loadGenerationRef.current) {
        fetchInFlightRef.current = false;
        if (mountedRef.current) {
          setPromotionsLoading(false);
        }
      }
    }
  }, [refreshCouponData, syncPromotionsFromList, userKey]);

  // Effect A — start coupon API refresh. Must NOT depend on playerCoupons.
  useEffect(() => {
    if (!userKey) {
      if (prevUserKeyRef.current) {
        resetFormState();
      }
      prevUserKeyRef.current = "";
      return;
    }

    // Auth-ready gate (same signal as CouponCashier).
    if (isLoadingUser || isRefreshingUserProfile) {
      setPromotionsLoading(true);
      setPromotionsError(null);
      return;
    }

    const nextKey = userKey;
    const prevKey = prevUserKeyRef.current;

    if (prevKey && prevKey !== nextKey) {
      setSelectedCoupon(null);
      clearLocalMessages();
      fetchedForUserRef.current = undefined;
      fetchInFlightRef.current = false;
      loadGenerationRef.current += 1;
      if (retryTimeoutRef.current != null) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }

    prevUserKeyRef.current = nextKey;

    if (fetchedForUserRef.current === nextKey) {
      // Already fetched successfully — never leave Loading stuck.
      setPromotionsLoading(false);
      return;
    }

    loadPromotions();
  }, [
    clearLocalMessages,
    isLoadingUser,
    isRefreshingUserProfile,
    loadPromotions,
    resetFormState,
    userKey,
  ]);

  // Effect B — sync context playerCoupons into local promo cards.
  // Never starts a new API request and never forces loading=true.
  useEffect(() => {
    if (!userKey) return;
    if (isLoadingUser || isRefreshingUserProfile) return;
    if (fetchedForUserRef.current !== userKey) return;

    syncPromotionsFromList(playerCoupons);
    setPromotionsLoading(false);
  }, [
    isLoadingUser,
    isRefreshingUserProfile,
    playerCoupons,
    syncPromotionsFromList,
    userKey,
  ]);

  // True unmount only — do not bump generation on Effect A/B re-runs.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadGenerationRef.current += 1;
      fetchInFlightRef.current = false;
      if (retryTimeoutRef.current != null) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedCoupon) return;
    if (isCouponAmountEligible(selectedCoupon, amount)) return;

    const minimum = Number(selectedCoupon.minDeposit) || 0;
    setSelectedCoupon(null);
    setCouponWarning(
      `The selected promotion was removed because it requires a minimum deposit of $${minimum}.`,
    );
  }, [amount, selectedCoupon]);

  useEffect(() => {
    if (gameLoader) return;
    freeSpinPlayLockRef.current = false;
    setIsStartingFreeSpin(false);
  }, [gameLoader]);

  const activeCouponCode = normalizeCouponCode(
    activeCoupon?.coupon_code || activeCoupon?.couponCode,
  );
  const activeRedeemType = getCouponRedeemType(activeCoupon);
  const isActiveFreeSpin = activeRedeemType === 3;
  const canCancelActiveCoupon =
    Boolean(activeCoupon) && !isActiveFreeSpin && Boolean(activeCouponCode);

  const activeFreeSpinDetails = useMemo(
    () => (isActiveFreeSpin ? getFreeSpinDetailsFromCoupon(activeCoupon) : null),
    [activeCoupon, isActiveFreeSpin],
  );

  const spinsRemaining = Number(
    activeCoupon?.spins_remaining ?? activeCoupon?.spinsRemaining ?? 0,
  );
  const freeSpinsCompleted =
    activeCoupon?.free_spins_completed === true ||
    activeCoupon?.freeSpinsCompleted === true;

  const showNoticeFreeSpinPlay =
    isActiveFreeSpin &&
    !freeSpinsCompleted &&
    spinsRemaining > 0 &&
    Boolean(activeFreeSpinDetails);

  const showQr = Boolean(
    paymentResponse &&
      !paymentResponse?.buyInUrl &&
      paymentResponse?.cryptoAddress,
  );

  const openFreeSpinModal = useCallback(
    (gameData) => {
      if (!canOpenFreeSpinModal(gameData)) return false;
      setFreeSpinGame(gameData);
      const el = document.getElementById("idFreeSpin");
      if (el) el.style.display = "block";
      return true;
    },
    [setFreeSpinGame],
  );

  const refreshActiveCoupon = useCallback(async () => {
    const response = await GetActiveCoupon();
    const next = resolveActiveCoupon(response);
    setActiveCoupon(next);
    setIsCouponActivated(resolveIsCouponActivated(next));
    return next;
  }, [setActiveCoupon, setIsCouponActivated]);

  const activateCouponCore = useCallback(
    async (coupon, { source, openFreeSpinOnSuccess }) => {
      const code = normalizeCouponCode(coupon?.code || coupon?.coupon_code);
      if (!code) throw new Error("Missing coupon code.");

      const redeemType = getCouponRedeemType(coupon);
      const freeSpin = redeemType === 3;
      const requiresVerifiedEmail = source === "deposit" && !freeSpin;

      if (requiresVerifiedEmail && isEmailVerifiedForCoupon(user) !== true) {
        const err = new Error(EMAIL_UNVERIFIED);
        err.code = EMAIL_UNVERIFIED;
        throw err;
      }

      setActivatingCouponCode(code);

      try {
        const response = await ActivateCoupon({ cuponCode: code });
        if (response?.error) {
          const err = new Error(response.message || "Coupon redeem failed.");
          err.data = { message: response.message };
          throw err;
        }

        const next = await refreshActiveCoupon();
        const nextCode = normalizeCouponCode(
          next?.coupon_code || next?.couponCode,
        );

        if (!next || nextCode !== code) {
          throw new Error("Coupon was not activated.");
        }

        if (getCouponRedeemType(next) === 3) {
          const gameData = mapActiveCouponToFreeSpinGame(next, games);
          setFreeSpinGame(gameData);
          if (openFreeSpinOnSuccess) {
            if (!canOpenFreeSpinModal(gameData)) {
              throw new Error("Free Spin game details are currently unavailable.");
            }
            openFreeSpinModal(gameData);
          }
        }

        return next;
      } finally {
        setActivatingCouponCode("");
      }
    },
    [games, openFreeSpinModal, refreshActiveCoupon, setFreeSpinGame, user],
  );

  const syncPromotionsAfterConflict = useCallback(async () => {
    if (typeof refreshCouponData !== "function") return;
    try {
      await refreshCouponData();
    } catch {
      // Best-effort refresh only — keep the original friendly error visible.
    }
  }, [refreshCouponData]);

  const prepareCouponBeforeDeposit = useCallback(async () => {
    if (!selectedCoupon) return;

    const selectedCode = normalizeCouponCode(selectedCoupon.code);
    if (selectedCode && selectedCode === activeCouponCode) return;

    if (getCouponRedeemType(selectedCoupon) === 3) {
      setSelectedCoupon(null);
      return;
    }

    if (!isCouponAmountEligible(selectedCoupon, amount)) {
      setSelectedCoupon(null);
      return;
    }

    await activateCouponCore(selectedCoupon, {
      source: "deposit",
      openFreeSpinOnSuccess: false,
    });
  }, [activateCouponCore, activeCouponCode, amount, selectedCoupon]);

  const validateActiveCouponMinDeposit = useCallback(() => {
    if (!activeCoupon) return true;
    const mapped = mapPlayerCoupon(activeCoupon);
    if (!isCouponAmountEligible(mapped, amount)) {
      const minimum = Number(mapped.minDeposit) || BASE_DEPOSIT_MIN;
      setAmountError(
        `The active promotion requires a minimum deposit of $${minimum}.`,
      );
      return false;
    }
    return true;
  }, [activeCoupon, amount]);

  const handleWalletDeposit = async (event) => {
    event.preventDefault();

    if (!user) {
      setIsClosed(false);
      return;
    }

    clearLocalMessages();

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setAmountError("Please enter a deposit amount.");
      return;
    }
    if (numericAmount < BASE_DEPOSIT_MIN) {
      setAmountError("The minimum deposit amount is $10.");
      return;
    }
    if (!validateActiveCouponMinDeposit()) return;

    setPaymentResponse(null);
    setWalletDeposit(null);
    setPageLoader(true);

    try {
      try {
        await prepareCouponBeforeDeposit();
      } catch (couponError) {
        const warning =
          couponError?.code === EMAIL_UNVERIFIED ||
          couponError?.message === EMAIL_UNVERIFIED
            ? resolveCouponWarning(EMAIL_UNVERIFIED)
            : getCouponErrorMessage(couponError, {
                context: "deposit",
                hasActiveCoupon: Boolean(activeCouponCode),
              });
        setCouponWarning(warning);
        if (isCouponConflictError(couponError)) {
          await syncPromotionsAfterConflict();
        }
      }

      try {
        const response = await Deposit({
          amount,
          currencyCode: "USD",
          cryptoCurrencyCode: "SATS",
          isBuyCrypto: false,
          providerName: "",
        });

        const invoice = response?.cryptoAddress || response?.CryptoAddress;
        if (!invoice) {
          setErrorMessagePayment("CryptoAddress is missing from response");
          return;
        }

        // Browsers can't report an unhandled `lightning:` link (Windows' own
        // "pick an app" dialog looks like a wallet opening), so the QR fallback
        // is always shown alongside the wallet attempt.
        setWalletDeposit({ ...response, cryptoAddress: invoice });
        openLightningWallet(invoice);
      } catch (paymentError) {
        setErrorMessagePayment(getPaymentErrorMessage(paymentError));
      }
    } finally {
      setPageLoader(false);
    }
  };

  const handleDeposit = async (event, isBuyCrypto, providerName = "") => {
    event.preventDefault();

    if (!user) {
      setIsClosed(false);
      return;
    }

    clearLocalMessages();

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setAmountError("Please enter a deposit amount.");
      return;
    }
    if (numericAmount < BASE_DEPOSIT_MIN) {
      setAmountError("The minimum deposit amount is $10.");
      return;
    }
    if (!cryptoCurrencyCode) {
      setCryptoError("Please select a cryptocurrency before continuing.");
      return;
    }
    if (!validateActiveCouponMinDeposit()) return;

    setPaymentResponse(null);
    setPageLoader(true);

    try {
      try {
        await prepareCouponBeforeDeposit();
      } catch (couponError) {
        const warning =
          couponError?.code === EMAIL_UNVERIFIED ||
          couponError?.message === EMAIL_UNVERIFIED
            ? resolveCouponWarning(EMAIL_UNVERIFIED)
            : getCouponErrorMessage(couponError, {
                context: "deposit",
                hasActiveCoupon: Boolean(activeCouponCode),
              });
        setCouponWarning(warning);
        if (isCouponConflictError(couponError)) {
          await syncPromotionsAfterConflict();
        }
      }

      try {
        const response = await Deposit({
          amount,
          currencyCode: "USD",
          cryptoCurrencyCode,
          isBuyCrypto,
          providerName: providerName || "",
        });
        setPaymentResponse(response);
        setIsFirstDepositClick(true);

        if (response?.buyInUrl) {
          window.open(response.buyInUrl, "_blank");
        } else {
          requestAnimationFrame(() => {
            paymentResultRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          });
        }
      } catch (paymentError) {
        setErrorMessagePayment(getPaymentErrorMessage(paymentError));
      }
    } finally {
      setPageLoader(false);
    }
  };

  const changeCrypto = async (event) => {
    const nextCrypto = event.target.value;
    setCryptoCurrencyCode(nextCrypto);
    setSelectedCrypto(nextCrypto);
    setCryptoError(null);

    if (!isFirstDepositClick || !amount || !nextCrypto) return;

    setPageLoader(true);
    setErrorMessagePayment(null);
    try {
      const response = await Deposit({
        amount,
        currencyCode: "USD",
        cryptoCurrencyCode: nextCrypto,
        isBuyCrypto: false,
        providerName: "",
      });
      setPaymentResponse(response);
    } catch (error) {
      setErrorMessagePayment(getPaymentErrorMessage(error));
    } finally {
      setPageLoader(false);
    }
  };

  const onCouponToggle = async (coupon, checked) => {
    clearLocalMessages();
    const code = normalizeCouponCode(coupon.code);
    const redeemType = getCouponRedeemType(coupon);

    if (activeCouponCode && activeCouponCode !== code) {
      setErrorCouponMessage(
        isActiveFreeSpin
          ? "Complete your active Free Spins promotion before activating another promotion."
          : "Cancel or complete your active promotion before selecting another one.",
      );
      return;
    }

    if (redeemType === 3) {
      if (!checked) return;
      if (activatingCouponCode) return;
      if (activeCouponCode === code) return;

      setPageLoader(true);
      try {
        await activateCouponCore(coupon, {
          source: "free-spin",
          openFreeSpinOnSuccess: true,
        });
        setSelectedCoupon(coupon);
        setSuccessCouponMessage("Free Spins activated.");
      } catch (error) {
        setCouponWarning(
          getCouponErrorMessage(error, {
            context: "free-spin",
            hasActiveCoupon: Boolean(activeCouponCode),
          }),
        );
        if (isCouponConflictError(error)) {
          await syncPromotionsAfterConflict();
        }
      } finally {
        setPageLoader(false);
      }
      return;
    }

    if (!checked) {
      setSelectedCoupon(null);
      return;
    }

    if (!isCouponAmountEligible(coupon, amount)) return;

    setSelectedCoupon(coupon);
    setSuccessCouponMessage(
      "Promotion selected. It will be applied when you deposit.",
    );
  };

  const onCancelActive = async () => {
    if (!canCancelActiveCoupon) return;
    clearLocalMessages();
    setPageLoader(true);
    try {
      await CancelCoupon({ couponCode: activeCoupon?.coupon_code });
      await refreshActiveCoupon();
      setSelectedCoupon(null);
      setSuccessCouponMessage("Active coupon cancelled.");
    } catch (error) {
      setErrorCouponMessage(
        getCouponErrorMessage(error, {
          context: "cancel",
          hasActiveCoupon: Boolean(activeCouponCode),
        }),
      );
      if (isCouponConflictError(error)) {
        await syncPromotionsAfterConflict();
      }
    } finally {
      setPageLoader(false);
    }
  };

  const onApplyBonusCode = async () => {
    const code = String(bonusCode || "").trim();
    if (!code) return;

    clearLocalMessages();

    if (activeCouponCode && activeCouponCode !== normalizeCouponCode(code)) {
      setErrorCouponMessage(
        isActiveFreeSpin
          ? "Complete your active Free Spins promotion before activating another promotion."
          : "Cancel or complete your active promotion before applying another code.",
      );
      return;
    }

    setBonusApplying(true);
    setPageLoader(true);
    try {
      const response = await ActivateCoupon({ cuponCode: code });
      if (response?.error) {
        const knownFreeSpin = promotions.some(
          (promo) =>
            normalizeCouponCode(promo.code) === normalizeCouponCode(code) &&
            getCouponRedeemType(promo) === 3,
        );
        throw Object.assign(new Error(response.message), {
          data: { message: response.message },
          isFreeSpinGuess: knownFreeSpin,
        });
      }

      const next = await refreshActiveCoupon();
      if (!next) throw new Error("Coupon was not activated.");

      if (getCouponRedeemType(next) === 3) {
        const gameData = mapActiveCouponToFreeSpinGame(next, games);
        setFreeSpinGame(gameData);
        if (!openFreeSpinModal(gameData)) {
          setErrorCouponMessage(
            "Free Spin game details are currently unavailable.",
          );
        } else {
          setSuccessCouponMessage("Free Spins activated.");
        }
      } else {
        setSelectedCoupon(mapPlayerCoupon(next));
        setSuccessCouponMessage("Bonus code applied.");
      }
      setBonusCode("");
    } catch (error) {
      if (error?.isFreeSpinGuess) {
        setCouponWarning(
          getCouponErrorMessage(error, {
            context: "free-spin",
            hasActiveCoupon: Boolean(activeCouponCode),
          }),
        );
      } else {
        setErrorCouponMessage(
          getCouponErrorMessage(error, {
            context: "bonus",
            hasActiveCoupon: Boolean(activeCouponCode),
          }),
        );
      }
      if (isCouponConflictError(error)) {
        await syncPromotionsAfterConflict();
      }
    } finally {
      setBonusApplying(false);
      setPageLoader(false);
    }
  };

  const playActiveFreeSpinGame = () => {
    if (freeSpinPlayLockRef.current || isStartingFreeSpin || gameLoader) return;

    const details = getFreeSpinDetailsFromCoupon(activeCoupon);
    if (!details) {
      setErrorCouponMessage("Free Spin game details are currently unavailable.");
      return;
    }

    freeSpinPlayLockRef.current = true;
    setIsStartingFreeSpin(true);

    startGame(details.gameId, details.machineId, details.displayName, () => {
      router.push(buildGamePathFromDisplayName(details.displayName));
    });

    const el = document.getElementById("idFreeSpin");
    if (el) el.style.display = "none";
  };

  const handleCopyAmount = async () => {
    const ok = await copyText(paymentResponse?.amountTo);
    if (!ok) return;
    setShowLabelAmount(true);
    setTimeout(() => setShowLabelAmount(false), 3000);
  };

  const handleCopyAddress = async () => {
    const ok = await copyText(paymentResponse?.cryptoAddress);
    if (!ok) return;
    setShowLabel(true);
    setTimeout(() => setShowLabel(false), 3000);
  };

  if (!user) return null;

  return (
    <section className="quickDeposit" aria-label="Quick Deposit">
      <div className="quickDeposit__inner">
        <h2 className="quickDeposit__title">Quick Deposit</h2>

        <form
          className="quickDeposit__form"
          onSubmit={(event) => event.preventDefault()}
        >
          <div className="quickDeposit__fields">
            <div className="quickDeposit__field">
              <label htmlFor="qd-crypto">Deposit Currency</label>
              <div className="quickDeposit__control ConSelectF_W">
                <select
                  id="qd-crypto"
                  className="NoSelect"
                  value={cryptoCurrencyCode}
                  onChange={changeCrypto}
                >
                  <option value="">Choose Cryptocurrency</option>
                  {CRYPTO_OPTIONS.map((code) => (
                    <option key={code} value={code} className={code}>
                      {code}
                    </option>
                  ))}
                </select>
                <span
                  className={`qdCryptoIcon ${selectedCrypto || "defaultClass"}`}
                  aria-hidden="true"
                />
              </div>
              {cryptoError ? (
                <p className="quickDeposit__fieldError" role="alert">
                  {cryptoError}
                </p>
              ) : null}
            </div>

            <div className="quickDeposit__field">
              <label htmlFor="qd-amount">Deposit Amount</label>
              <div className="quickDeposit__control quickDeposit__amountControl">
                <input
                  id="qd-amount"
                  name="amount"
                  type="number"
                  min={BASE_DEPOSIT_MIN}
                  value={amount}
                  placeholder="Enter Amount"
                  onChange={(event) => {
                    setAmount(event.target.value);
                    setAmountError(null);
                  }}
                />
                <span className="qdMinBadge" title={`$${BASE_DEPOSIT_MIN} MIN`}>
                  ${BASE_DEPOSIT_MIN} MIN
                </span>
              </div>
              {amountError ? (
                <p className="quickDeposit__fieldError" role="alert">
                  {amountError}
                </p>
              ) : (
                <p className="quickDeposit__minHint">
                  Minimum deposit: ${BASE_DEPOSIT_MIN}
                </p>
              )}
            </div>
          </div>

          <div className="quickDeposit__quickAmounts">
            {QUICK_AMOUNTS.map((value) => (
              <button
                key={value}
                type="button"
                className={`quickDeposit__amountBtn${
                  Number(amount) === Number(value) ? " is-active" : ""
                }`}
                onClick={() => {
                  setAmount(value);
                  setAmountError(null);
                }}
              >
                ${value}
              </button>
            ))}
          </div>

          <div className="quickDeposit__actions">
            <button
              type="button"
              className="quickDeposit__actionBtn quickDeposit__primaryBtn"
              onClick={(event) => handleDeposit(event, false, "")}
            >
              <img
                src="/assets/quick-deposit-bitcoin-icon.png"
                alt=""
                aria-hidden="true"
                className="quickDeposit__btnIcon"
              />
              CRYPTO DEPOSIT
            </button>
            <div className="quickDeposit__methods">
              <button
                type="button"
                className="quickDeposit__actionBtn quickDeposit__methodBtn"
                onClick={(event) => handleDeposit(event, true, "PayPal")}
              >
                <img
                  src="/assets/quick-deposit-paypal-icon.png"
                  alt=""
                  aria-hidden="true"
                  className="quickDeposit__btnIcon"
                />
                PAYPAL
              </button>
              <button
                type="button"
                className="quickDeposit__actionBtn quickDeposit__methodBtn"
                onClick={(event) => handleDeposit(event, true, "Moonpay")}
              >
                <img
                  src="/assets/quick-deposit-moonpay-icon.png"
                  alt=""
                  aria-hidden="true"
                  className="quickDeposit__btnIcon"
                />
                MOONPAY
              </button>
              <button
                type="button"
                className="quickDeposit__actionBtn quickDeposit__methodBtn"
                onClick={handleWalletDeposit}
              >
                <FaWallet aria-hidden="true" className="quickDeposit__btnIcon" />
                WALLET
              </button>
            </div>
          </div>

          {walletDeposit && (
            <WalletDepositModal
              deposit={walletDeposit}
              onClose={closeWalletDeposit}
            />
          )}

          {errorMessagePayment && (
            <div className="error ErrorBlink quickDeposit__error">
              {errorMessagePayment}
            </div>
          )}
          {couponWarning && (
            <div className="quickDeposit__couponWarning">{couponWarning}</div>
          )}
          {errorCouponMessage && (
            <div className="error ErrorBlink quickDeposit__error">
              {errorCouponMessage}
            </div>
          )}
          {successCouponMessage && (
            <div className="quickDeposit__success">{successCouponMessage}</div>
          )}

          {showQr && (
            <div
              className="quickDeposit__paymentResult"
              ref={paymentResultRef}
              id="CryptoAddress_QR_Code"
            >
              <div className="quickDeposit__qr BoxQR">
                <div className="BoxContImgQR">
                  <QRCodeSVG
                    level="H"
                    value={paymentResponse?.cryptoAddress}
                  />
                </div>
              </div>
              <div className="quickDeposit__paymentMeta">
                <span className="MiniRuleCFrom">Crypto Amount:</span>
                <textarea
                  id="qd-CryptoAmount"
                  rows="1"
                  disabled
                  value={paymentResponse?.amountTo || ""}
                  readOnly
                />
                {showLabelAmount && (
                  <p className="LabelWarning CodeBlink Q">
                    The crypto amount was copied!!
                  </p>
                )}
                <button
                  type="button"
                  className="BtnCopy Cyan"
                  onClick={handleCopyAmount}
                >
                  Copy Amount
                </button>

                <span className="MiniRuleCFrom">Deposit Address:</span>
                <textarea
                  id="qd-AddressUrl"
                  rows="1"
                  disabled
                  value={paymentResponse?.cryptoAddress || ""}
                  readOnly
                />
                {showLabel && (
                  <p className="LabelWarning CodeBlink">
                    The address was copied!!
                  </p>
                )}
                <button
                  type="button"
                  className="BtnCopy Cyan"
                  onClick={handleCopyAddress}
                >
                  Copy Address
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="quickDeposit__promos">
          <div className="quickDeposit__promosHead">
            <h3>Available Promotions</h3>
            <span className="quickDeposit__liveBadge">LIVE</span>
          </div>

          {activeCoupon && (
            <div className="quickDeposit__notice">
              {isActiveFreeSpin ? (
                <div className="quickDeposit__freeSpinNotice">
                  <div className="quickDeposit__freeSpinNoticeText">
                    <strong>Free Spins Active</strong>
                    <span>
                      {spinsRemaining} spin{spinsRemaining === 1 ? "" : "s"}{" "}
                      remaining
                    </span>
                  </div>
                  {showNoticeFreeSpinPlay && (
                    <button
                      type="button"
                      className="quickDeposit__freeSpinPlay"
                      disabled={isStartingFreeSpin || gameLoader}
                      onClick={playActiveFreeSpinGame}
                    >
                      {isStartingFreeSpin || gameLoader ? "Starting…" : "Play"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="quickDeposit__activeNotice">
                  <span>
                    Active coupon:{" "}
                    <strong>
                      {String(
                        activeCoupon.coupon_code || activeCoupon.couponCode || "",
                      ).trim()}
                    </strong>
                  </span>
                  {canCancelActiveCoupon && (
                    <button
                      type="button"
                      className="quickDeposit__cancelActive"
                      onClick={onCancelActive}
                    >
                      Cancel active coupon
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="quickDeposit__promoList">
            {promotionsLoading && (
              <p className="quickDeposit__promoState">Loading promotions…</p>
            )}
            {!promotionsLoading && promotionsError && (
              <p className="quickDeposit__promoState">
                Promotions unavailable right now.
              </p>
            )}
            {!promotionsLoading &&
              !promotionsError &&
              promotions.length === 0 && (
                <p className="quickDeposit__promoState">
                  No available promotions at the moment.
                </p>
              )}

            {!promotionsLoading &&
              !promotionsError &&
              promotions.map((coupon) => {
                const code = normalizeCouponCode(coupon.code);
                const isActive = Boolean(activeCouponCode) && activeCouponCode === code;
                const isSelected =
                  !isActive &&
                  normalizeCouponCode(selectedCoupon?.code) === code;
                const redeemType = getCouponRedeemType(coupon);
                const isFreeSpin = redeemType === 3;
                const amountLocked = !isCouponAmountEligible(coupon, amount);
                const expanded = expandedCouponCode === code;
                const terms = formatCouponTerms(coupon.description);
                const lockedOn = isActive && isFreeSpin;
                const toggleOn = isActive || isSelected || lockedOn;
                const toggleDisabled =
                  amountLocked ||
                  lockedOn ||
                  Boolean(activatingCouponCode) ||
                  (Boolean(activeCouponCode) && !isActive);

                const extraRows = [];
                if (coupon.minDeposit > 0 && !amountLocked) {
                  const minLabel = `Min Deposit: $${coupon.minDeposit}`;
                  if (!descriptionHasTerm(terms, "min deposit")) {
                    extraRows.push(minLabel);
                  }
                }
                if (
                  coupon.bankMethod?.bonus != null &&
                  !descriptionHasTerm(terms, "bonus")
                ) {
                  extraRows.push(`Bonus: ${coupon.bankMethod.bonus}%`);
                }
                if (
                  coupon.bankMethod?.maximum_deposit != null &&
                  !descriptionHasTerm(terms, "max")
                ) {
                  extraRows.push(
                    `Max Deposit: $${coupon.bankMethod.maximum_deposit}`,
                  );
                }

                const termsPanelId = `qd-terms-${code || coupon.title}`;

                return (
                  <div
                    key={code || coupon.title}
                    className={[
                      "quickDeposit__promoRow",
                      isActive ? "is-active" : "",
                      isSelected ? "is-selected" : "",
                      amountLocked ? "is-amount-locked is-locked" : "",
                      expanded ? "is-expanded" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div className="quickDeposit__promoRowMain">
                      <div className="quickDeposit__promoInfo">
                        {coupon.image ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            className="quickDeposit__promoImg"
                            src={coupon.image}
                            alt=""
                          />
                        ) : (
                          <span className="quickDeposit__promoIcon" />
                        )}
                        <div className="quickDeposit__promoCopy">
                          <span className="quickDeposit__promoTitleWrap">
                            <span
                              className="quickDeposit__promoTitle"
                              aria-label={coupon.title}
                            >
                              {coupon.title}
                            </span>
                            {isActive && (
                              <span className="quickDeposit__promoBadge is-active">
                                Active
                              </span>
                            )}
                            {isSelected && (
                              <span className="quickDeposit__promoBadge is-selected">
                                Selected
                              </span>
                            )}
                          </span>

                          {code ? (
                            <span
                              className="quickDeposit__promoCode"
                              aria-label={`Coupon code ${code}`}
                            >
                              {code}
                            </span>
                          ) : null}

                          {amountLocked && (
                            <p className="quickDeposit__promoLockMsg">
                              <span
                                className="quickDeposit__promoLock"
                                aria-hidden="true"
                              >
                                🔒
                              </span>
                              <span>
                                Deposit at least ${coupon.minDeposit} to unlock
                              </span>
                            </p>
                          )}

                          <button
                            type="button"
                            className={`quickDeposit__termsToggle${
                              expanded ? " is-open" : ""
                            }`}
                            aria-expanded={expanded}
                            aria-controls={termsPanelId}
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedCouponCode(expanded ? "" : code);
                            }}
                          >
                            <span>
                              {expanded ? "Hide terms" : "View terms"}
                            </span>
                            <FaChevronDown
                              className="quickDeposit__termsChevron"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </div>

                      <label
                        className={[
                          "quickDeposit__redeemToggle",
                          toggleOn ? "is-on" : "",
                          lockedOn ? "is-locked-on" : "",
                          toggleDisabled ? "is-disabled" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <span className="quickDeposit__redeemToggleLabel">
                          {isActive
                            ? "Active"
                            : isFreeSpin
                              ? "Redeem Free Spins"
                              : "Redeem Now!"}
                        </span>
                        <input
                          type="checkbox"
                          checked={toggleOn}
                          disabled={toggleDisabled}
                          onChange={(event) =>
                            onCouponToggle(coupon, event.target.checked)
                          }
                        />
                        <span className="quickDeposit__redeemSwitch" />
                      </label>
                    </div>

                    {expanded && (
                      <div
                        className="quickDeposit__promoDetails"
                        id={termsPanelId}
                      >
                        <ul className="quickDeposit__termsList">
                          {terms.map((line, index) =>
                            renderCouponTermLine(line, `${code}-term-${index}`),
                          )}
                          {extraRows.map((line, index) =>
                            renderCouponTermLine(line, `${code}-extra-${index}`),
                          )}
                        </ul>
                        {coupon.termsUrl ? (
                          <a
                            className="quickDeposit__termsLink"
                            href={coupon.termsUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => event.stopPropagation()}
                          >
                            Terms &amp; Conditions
                          </a>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          <p className="quickDeposit__applyHint">
            * A coupon is applied automatically right before your deposit.
          </p>

          <div className="quickDeposit__bonus">
            <button
              type="button"
              className={`quickDeposit__bonusToggle${
                bonusCodeOpen ? " is-open" : ""
              }`}
              onClick={() => setBonusCodeOpen((open) => !open)}
              aria-expanded={bonusCodeOpen}
              aria-controls="qd-bonus-code-panel"
            >
              <span className="quickDeposit__bonusToggleLabel">
                I have a bonus code
              </span>
              <FaChevronDown
                className="quickDeposit__bonusChevron"
                aria-hidden="true"
              />
            </button>
            {bonusCodeOpen && (
              <div className="quickDeposit__bonusRow" id="qd-bonus-code-panel">
                <input
                  type="text"
                  value={bonusCode}
                  placeholder="Enter bonus code"
                  onChange={(event) => setBonusCode(event.target.value)}
                />
                <button
                  type="button"
                  className="quickDeposit__bonusApply"
                  disabled={bonusApplying}
                  onClick={onApplyBonusCode}
                >
                  {bonusApplying ? "Applying…" : "Apply"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
